import React, { useState } from 'react';
import styled from 'styled-components';
import { Camera, Bell, Check } from 'lucide-react';
import {
  requestCameraPermission,
  requestNotificationPermission,
  markOnboardingShown
} from '../../services/permissions';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 9999;
`;

const Sheet = styled.div`
  background: #fff;
  border-radius: 20px;
  width: 100%;
  max-width: 420px;
  padding: 28px 24px 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  animation: slideUp 0.25s ease-out;

  @keyframes slideUp {
    from { transform: translateY(20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
`;

const Title = styled.h2`
  font-size: 22px;
  margin: 0 0 8px;
  color: ${props => props.theme.colors.primary};
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: ${props => props.theme.colors.text.secondary};
  margin: 0 0 20px;
  text-align: center;
  line-height: 1.4;
`;

const PermissionRow = styled.div`
  display: flex;
  gap: 14px;
  padding: 14px;
  margin-bottom: 10px;
  border-radius: 12px;
  background: #f6f9fc;
  border: 1px solid ${props => props.granted ? '#34A85333' : 'transparent'};
`;

const IconWrap = styled.div`
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: 12px;
  background: ${props => props.granted ? '#34A85322' : props.theme.colors.primary + '22'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.granted ? '#34A853' : props.theme.colors.primary};
`;

const RowText = styled.div`
  flex: 1;
  min-width: 0;

  h4 {
    font-size: 15px;
    margin: 0 0 4px;
    color: ${props => props.theme.colors.text.primary};
  }

  p {
    font-size: 13px;
    color: ${props => props.theme.colors.text.secondary};
    margin: 0;
    line-height: 1.35;
  }
`;

const CheckBadge = styled.div`
  color: #34A853;
  display: flex;
  align-items: center;
`;

const PrimaryButton = styled.button`
  width: 100%;
  padding: 14px;
  background: ${props => props.theme.colors.primary};
  color: #fff;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 8px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SkipButton = styled.button`
  width: 100%;
  padding: 10px;
  background: transparent;
  color: ${props => props.theme.colors.text.secondary};
  border: none;
  font-size: 13px;
  cursor: pointer;
  margin-top: 6px;
`;

export const PermissionsOnboarding = ({ onDone }) => {
  const [cameraStatus, setCameraStatus] = useState('pending');
  const [notificationsStatus, setNotificationsStatus] = useState('pending');
  const [loading, setLoading] = useState(false);

  const handleAllow = async () => {
    setLoading(true);

    if (cameraStatus !== 'granted') {
      const cam = await requestCameraPermission();
      setCameraStatus(cam);
    }

    if (notificationsStatus !== 'granted') {
      const notif = await requestNotificationPermission();
      setNotificationsStatus(notif);
    }

    setLoading(false);
    markOnboardingShown();
    if (onDone) onDone();
  };

  const handleSkip = () => {
    markOnboardingShown();
    if (onDone) onDone();
  };

  const camGranted = cameraStatus === 'granted';
  const notifGranted = notificationsStatus === 'granted';

  return (
    <Overlay>
      <Sheet>
        <Title>Bienvenido a NomoFarma</Title>
        <Subtitle>
          Para aprovechar la app al máximo necesitamos dos permisos. Son rápidos y puedes cambiarlos después en ajustes.
        </Subtitle>

        <PermissionRow granted={camGranted}>
          <IconWrap granted={camGranted}>
            <Camera size={22} />
          </IconWrap>
          <RowText>
            <h4>Cámara</h4>
            <p>Para escanear códigos de barras de tus productos y añadirlos al inventario al instante.</p>
          </RowText>
          {camGranted && <CheckBadge><Check size={20} /></CheckBadge>}
        </PermissionRow>

        <PermissionRow granted={notifGranted}>
          <IconWrap granted={notifGranted}>
            <Bell size={22} />
          </IconWrap>
          <RowText>
            <h4>Notificaciones</h4>
            <p>Para avisarte de productos a punto de vencer, bajo stock y recordatorios importantes.</p>
          </RowText>
          {notifGranted && <CheckBadge><Check size={20} /></CheckBadge>}
        </PermissionRow>

        <PrimaryButton onClick={handleAllow} disabled={loading}>
          {loading ? 'Solicitando...' : 'Permitir'}
        </PrimaryButton>
        <SkipButton onClick={handleSkip} disabled={loading}>
          Ahora no
        </SkipButton>
      </Sheet>
    </Overlay>
  );
};
